import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { ShieldCheck, Loader2, ChevronRight, ChevronDown, RotateCcw, Ban, Smartphone, Copy } from 'lucide-react';

const fmtTs = (iso) => iso ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

const REASONS = {
  before_send: { label: 'vor Versand', cls: 'text-emerald-300 border-emerald-700/50 bg-emerald-500/10', title: 'Gescannt, bevor der Brief als versendet markiert wurde — interner QR-Test' },
  not_written: { label: 'Brief noch nicht geschrieben', cls: 'text-emerald-300 border-emerald-700/50 bg-emerald-500/10', title: 'Der Brief war zum Scan-Zeitpunkt noch nicht von der iAuto geschrieben — QR-Test aus der Vorschau/PDF' },
  same_day: { label: 'kurz nach dem Schreiben', cls: 'text-emerald-300 border-emerald-700/50 bg-emerald-500/10', title: 'Scan innerhalb 24 h nach dem Schreiben, Brief noch nicht als versendet markiert — QR-Test am Schreibtisch' },
  tester: { label: 'Test-Gerät', cls: 'text-sky-300 border-sky-700/50 bg-sky-500/10', title: 'Scan von einem registrierten Test-Gerät des Teams' },
  team: { label: 'aus der App geöffnet', cls: 'text-slate-300 border-slate-600/60 bg-slate-500/10', title: 'Ein eingeloggtes Teammitglied hat die Seite geöffnet — kein Scan' },
  bot: { label: 'Bot / Link-Vorschau', cls: 'text-slate-300 border-slate-600/60 bg-slate-500/10', title: 'Automatischer Abruf (z. B. Link-Vorschau eines Messengers) — kein Scan' },
  manual: { label: 'manuell ausgeschlossen', cls: 'text-amber-300 border-amber-700/50 bg-amber-500/10', title: 'Von einem Admin aus der Statistik genommen' },
  legacy_rule: { label: 'alte Versandtag-Regel', cls: 'text-rose-300 border-rose-700/50 bg-rose-500/10', title: 'Nach dem Versand gescannt, aber noch als Test markiert — bitte zurückholen' },
  restored: { label: 'zählt wieder', cls: 'text-sky-300 border-sky-700/50 bg-sky-500/10', title: 'War als Test markiert und wurde nachträglich als echter Scan eingestuft' },
};

function ScanRow({ s, i, isAdmin, busy, onRestore, onExclude, navigate }) {
  const r = REASONS[s.reason] || REASONS.before_send;
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs py-1.5 px-2 rounded-sm hover:bg-secondary/30 transition-colors" data-testid={`test-scan-row-${i}`}>
      <button onClick={() => s.project_id && navigate(`/projects/${s.project_id}`)} data-testid={`test-scan-company-${i}`}
        className="font-medium text-foreground hover:text-blue-400 transition-colors truncate max-w-[240px] text-left">
        {s.company_name || '—'}{s.source === 'breakup' ? <span className="ml-1 text-[9px] font-mono text-amber-300">Break-up</span> : null}
      </button>
      <span className={`text-[9px] px-1.5 py-0.5 rounded border font-mono uppercase ${r.cls}`} title={r.title} data-testid={`test-scan-reason-${i}`}>{r.label}</span>
      <span className="font-mono text-muted-foreground" data-testid={`test-scan-at-${i}`}>Scan {fmtTs(s.at)}</span>
      <span className="font-mono text-muted-foreground/70">· versendet {s.letter_sent_at ? fmtTs(s.letter_sent_at) : 'noch nicht'}</span>
      {(s.device || s.where || s.by_user) && (
        <span className="text-[10px] text-muted-foreground/80" data-testid={`test-scan-device-${i}`}>
          · {[s.by_user, s.device, s.where].filter(Boolean).join(' · ')}
        </span>
      )}
      {s.reason === 'restored' && s.restored_by && <span className="text-[10px] text-muted-foreground/70">· zurückgeholt von {s.restored_by}</span>}
      {s.reason === 'manual' && s.excluded_by && <span className="text-[10px] text-muted-foreground/70">· von {s.excluded_by}</span>}
      {isAdmin && !s.internal && (
        <span className="ml-auto shrink-0">
          {s.is_test ? (
            <button onClick={() => onRestore(s)} disabled={busy === s.visit_id} data-testid={`test-scan-restore-${i}`}
              className="inline-flex items-center gap-1 text-[10px] text-sky-300 hover:text-sky-200 border border-sky-700/50 rounded-sm px-2 py-0.5 disabled:opacity-50">
              {busy === s.visit_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />} Doch echter Scan
            </button>
          ) : (
            <button onClick={() => onExclude(s)} disabled={busy === s.visit_id} data-testid={`test-scan-exclude-${i}`}
              className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-amber-300 border border-border rounded-sm px-2 py-0.5 disabled:opacity-50">
              {busy === s.visit_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Ban className="w-3 h-3" />} Doch Test
            </button>
          )}
        </span>
      )}
    </div>
  );
}

function TestDeviceBlock({ userName }) {
  const url = `${window.location.origin}/test-geraet?name=${encodeURIComponent(userName || '')}`;
  const copy = () => navigator.clipboard?.writeText(url).then(() => toast.success('Link kopiert')).catch(() => toast.error('Kopieren fehlgeschlagen'));
  return (
    <div className="flex flex-wrap items-start gap-3 border border-sky-700/40 bg-sky-500/5 rounded-sm p-2.5 mx-2 mb-2" data-testid="test-device-block">
      <div className="bg-white p-1.5 rounded-sm shrink-0" data-testid="test-device-qr"><QRCodeSVG value={url} size={72} /></div>
      <div className="flex-1 min-w-[200px] space-y-1">
        <p className="text-xs font-medium text-foreground flex items-center gap-1.5"><Smartphone className="w-3.5 h-3.5 text-sky-300" /> Dein Handy als Test-Gerät registrieren</p>
        <p className="text-[11px] text-muted-foreground">
          QR-Code mit der Kamera deines Handys scannen (öffnet Safari/Chrome) → „Registrieren“. Danach zählt jeder QR-Scan von diesem Handy
          als Test — auch bei Break-up-Briefen, deren Erstansprache längst versendet ist. Gilt pro Browser, 1 Jahr.
        </p>
        <button onClick={copy} data-testid="test-device-copy" className="inline-flex items-center gap-1 text-[10px] text-sky-300 hover:text-sky-200">
          <Copy className="w-3 h-3" /> Link kopieren
        </button>
      </div>
    </div>
  );
}

export function TestScanAuditCard({ onChanged }) {
  const navigate = useNavigate();
  const { isAdmin, user } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState('');

  const load = useCallback(() => { api.getTestScans().then(r => setData(r.data)).catch(() => setData({ scans: [], test_count: 0, restored_count: 0 })); }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (s, fn, msg) => {
    setBusy(s.visit_id);
    try { await fn(s.visit_id); toast.success(`${s.company_name}: ${msg}`); load(); onChanged?.(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Aktion fehlgeschlagen'); }
    finally { setBusy(''); }
  };
  const onRestore = (s) => window.confirm(`${s.company_name}: Scan vom ${fmtTs(s.at)} doch als echten Scan zählen?`) && act(s, api.restoreLandingVisit, 'Scan zählt wieder');
  const onExclude = (s) => window.confirm(`${s.company_name}: Scan vom ${fmtTs(s.at)} als Test aus der Statistik nehmen?`) && act(s, api.excludeLandingVisit, 'Scan als Test markiert');

  const scans = data?.scans || [];
  const legacy = scans.filter(s => s.reason === 'legacy_rule').length;
  return (
    <div className="bg-card border border-border rounded-sm" data-testid="test-scan-audit-card">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center gap-2 px-5 py-3.5 text-left" data-testid="test-scan-audit-toggle">
        {open ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
        <ShieldCheck className="w-4 h-4 text-emerald-400" />
        <span className="text-sm font-medium text-foreground">Test-Scans prüfen</span>
        <span className="text-xs text-muted-foreground" data-testid="test-scan-audit-summary">
          {data ? `${data.test_count} ausgeschlossen · ${data.restored_count} nachträglich gezählt${data.internal_count ? ` · ${data.internal_count}× aus der App/Bot` : ''}` : '…'}
        </span>
        {legacy > 0 && <span className="text-[10px] text-rose-300 border border-rose-700/50 rounded-sm px-1.5 py-0.5" data-testid="test-scan-legacy-warning">{legacy} noch nach alter Regel markiert</span>}
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-1">
          <p className="text-[11px] text-muted-foreground px-2 pb-1">
            Regeln: Nach „als versendet markiert“ ist jeder Scan echt. Davor gilt: Brief noch nicht geschrieben oder Scan innerhalb 24 h nach dem Schreiben = Test;
            später = echt (der Brief ist unterwegs — mit Hinweis „bitte als versendet markieren“). Scans von registrierten Test-Geräten und Öffnen aus der App zählen nie.
            {isAdmin ? ' Falsch einsortierte Scans können hier zurückgeholt oder ausgeschlossen werden.' : ''}
          </p>
          <TestDeviceBlock userName={user?.name} />
          <div className="max-h-[420px] overflow-y-auto space-y-0.5" data-testid="test-scan-list">
          {!data ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground mx-2" />
            : scans.length === 0 ? <p className="text-xs text-muted-foreground px-2" data-testid="test-scan-empty">Keine Test-Scans vorhanden.</p>
            : scans.map((s, i) => <ScanRow key={s.visit_id} s={s} i={i} isAdmin={isAdmin} busy={busy} onRestore={onRestore} onExclude={onExclude} navigate={navigate} />)}
          </div>
        </div>
      )}
    </div>
  );
}
